import { useEffect, useState } from 'react';
import { appleMusicUrl, type PublicGuest, type PublicTrack } from '@karozik/shared';
import { api, Avatar, formatClock, Spinner, TrackLine } from '@karozik/ui';
import type { AdminEvent } from '../types';

interface Recap {
  tracksPlayed: number;
  guestTracksPlayed: number;
  totalReactions: number;
  guests: number;
  badges: { id: string; emoji: string; title: string; description: string; guest: PublicGuest | null }[];
  playlist: { track: PublicTrack | null; startedAt: number; proposer: PublicGuest | null; dedication: string | null }[];
}

export function RecapTab({ event }: { event: AdminEvent }) {
  const [recap, setRecap] = useState<Recap | null>(null);
  useEffect(() => {
    void api<Recap>(`/api/admin/events/${event.id}/recap`).then(setRecap);
  }, [event.id]);
  if (!recap) return <Spinner />;

  const text = recap.playlist
    .filter((p) => p.track)
    .map((p) => `${formatClock(p.startedAt)}  ${p.track!.title} — ${p.track!.artist}  ${appleMusicUrl(p.track!.id)}`)
    .join('\n');

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-2 text-center">
        {[
          [recap.tracksPlayed, 'titres'],
          [recap.guestTracksPlayed, 'des invités'],
          [recap.totalReactions, 'réactions'],
          [recap.guests, 'invités'],
        ].map(([v, l]) => (
          <div key={l} className="rounded-2xl bg-surface p-3">
            <div className="font-display text-2xl font-bold text-gradient">{v}</div>
            <div className="text-xs text-ink-dim">{l}</div>
          </div>
        ))}
      </div>
      <ul className="space-y-2">
        {recap.badges.map((b) => (
          <li key={b.id} className="flex items-center gap-3 rounded-2xl bg-surface p-3">
            <span className="text-2xl">{b.emoji}</span>
            <div className="flex-1">
              <div className="font-semibold">{b.title}</div>
              <div className="text-xs text-ink-dim">{b.description}</div>
            </div>
            {b.guest && (
              <>
                <Avatar guest={b.guest} size="sm" /> {b.guest.pseudo}
              </>
            )}
          </li>
        ))}
      </ul>
      <button onClick={() => void navigator.clipboard.writeText(text)} className="w-full rounded-xl bg-surface-2 py-2 text-sm">
        📋 Copier la playlist (texte + liens Apple Music)
      </button>
      <ol className="space-y-1.5">
        {recap.playlist.map(
          (p, i) =>
            p.track && (
              <li key={i} className="flex items-center gap-2 rounded-2xl bg-surface p-2">
                <span className="w-11 text-xs text-ink-faint">{formatClock(p.startedAt)}</span>
                <TrackLine track={p.track} artSize="h-9 w-9" sub={p.proposer && <div className="text-xs text-ink-faint">{p.proposer.emoji} {p.proposer.pseudo}</div>} />
              </li>
            ),
        )}
      </ol>
    </div>
  );
}
